
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';

interface SecurityEvent {
  event_type: string;
  event_details?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
}

export const useSecurityMonitoring = () => {
  const { user } = useAuth();

  const logSecurityEvent = async (event: SecurityEvent) => {
    if (!user) return;

    try {
      // Get client IP and user agent (in a real app, this would come from server)
      const clientInfo = {
        ip_address: null, // Would be populated by server
        user_agent: navigator.userAgent.substring(0, 500) // Truncate to prevent abuse
      };

      await backendClient.functions.invoke('log-security-event', {
        body: {
          user_id: user.id,
          event_type: event.event_type,
          event_details: event.event_details || {},
          ...clientInfo
        }
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
      // Don't throw - security logging shouldn't break app functionality
    }
  };

  const logAuthAttempt = (success: boolean, method: string) => {
    logSecurityEvent({
      event_type: success ? 'auth_success' : 'auth_failure',
      event_details: { method, timestamp: new Date().toISOString() }
    });
  };

  const logSuspiciousActivity = (activity: string, details?: Record<string, any>) => {
    logSecurityEvent({
      event_type: 'suspicious_activity',
      event_details: { activity, ...details, timestamp: new Date().toISOString() }
    });
  };

  const logRateLimitHit = (action: string, limit: number) => {
    logSecurityEvent({
      event_type: 'rate_limit_exceeded',
      event_details: { action, limit, timestamp: new Date().toISOString() }
    });
  };

  return {
    logSecurityEvent,
    logAuthAttempt,
    logSuspiciousActivity,
    logRateLimitHit
  };
};
